/**
 * Figma API module — OAuth connect and per-user token management.
 *
 * Deliberately holds NO file, page, or comment reads. Every one of those
 * routes resolves the CALLER's own Figma token server-side
 * (`packages/server/src/routes/figma/files.ts:139-142`), which makes them
 * per-user; the dashboard's only Figma-linked surface is the issue page, and
 * the file link it renders is a per-PROJECT fact. Calling them on behalf of a
 * viewer who never connected Figma could only ever return 400 NOT_CONNECTED,
 * which is exactly what happened to an invited teammate on 2026-09-01 (21
 * console errors in one session, 38 fake `$exception` events, no reachable
 * message). The issue page now renders Figma's own tokenless embed instead.
 * The routes themselves stay — the extension is a legitimate caller.
 */
import { fetchApi } from './client';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

export const figmaApi = {
  getAuthUrl: (token: string | undefined, userId: string) =>
    fetchApi<{ url: string; state: string }>('/figma/oauth/auth', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ userId }),
    }),

  // Token management — routes are scoped to the authenticated user via /me.
  // The server reads the userId from the Clerk JWT / extension session in
  // the auth middleware, so no userId is passed in the URL or body. The
  // old `/figma/tokens/:userId` shape was an unauthenticated IDOR (any
  // caller could read any user's Figma token by guessing a UUID).
  // includeUser=1 asks the server to resolve the Figma account identity
  // (GET /v1/me server-side, fail-open null) so Settings can show
  // "Connected as {handle}" like every other integration card (OQA-12).
  // The extension's connect poll calls the bare route and triggers no
  // Figma API call.
  getToken: (token: string | undefined) =>
    fetchApi<{
      accessToken: string;
      expiresAt?: string;
      user?: { email: string; handle: string; imgUrl: string | null } | null;
    } | null>(
      '/figma/tokens/me?includeUser=1',
      { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }
    ).catch((err) => {
      // Return null for legacy 404 responses from older server builds.
      if (err.message?.includes('No Figma token found')) return null;
      throw err;
    }),

  removeToken: (token: string | undefined) =>
    fetchApi<{ deleted: boolean }>('/figma/tokens/me', {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),
};
